"use client";

import { createContext, useCallback, useContext, useState } from "react";

type ToastType = "success" | "error";

interface Toast {
  type: ToastType;
  text: string;
}

interface ToastContextValue {
  toast: Toast | null;
  showToast: (type: ToastType, text: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toast: null,
  showToast: () => {},
});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((type: ToastType, text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), type === "error" ? 10000 : 5000);
  }, []);

  return (
    <ToastContext.Provider value={{ toast, showToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function ToastOverlay() {
  const { toast } = useToast();

  if (!toast) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 h-[50px] flex items-center justify-center z-50">
      <div
        className={`px-6 py-2 rounded-md text-sm font-medium shadow-lg ${
          toast.type === "success"
            ? "bg-green-600 text-white"
            : "bg-red-600 text-white"
        }`}
      >
        {toast.text}
      </div>
    </div>
  );
}
